// No need to import API_BASE_URL as we'll use relative URLs like the other services

export interface CookieConsentResponse {
    consent_id: number;
    user_id?: string;
    session_id?: string;
    necessary_cookies: boolean;
    analytics_cookies: boolean;
    functional_cookies: boolean;
    marketing_cookies: boolean;
    third_party_cookies: boolean;
    consent_version: string;
    privacy_policy_version: string;
    consent_language: string;
    consent_given_at: string;
    consent_expires_at: string;
    is_expired: boolean;
    is_active: boolean;
}

export interface CreateConsentRequest {
    analytics_cookies: boolean;
    functional_cookies: boolean;
    marketing_cookies: boolean;
    third_party_cookies: boolean;
    consent_language?: string;
}

export interface UpdateConsentRequest {
    analytics_cookies?: boolean;
    functional_cookies?: boolean;
    marketing_cookies?: boolean;
    third_party_cookies?: boolean;
}

export interface CategoryConsentResponse {
    category: string;
    has_consent: boolean;
    is_expired: boolean;
}

export interface CookiePolicyResponse {
    version: string;
    last_updated: string;
    categories: CookieCategoryInfo[];
    legal_basis: LegalBasisInfo[];
    user_rights: UserRightInfo[];
}

export interface CookieCategoryInfo {
    category: string;
    name: string;
    description: string;
    essential: boolean;
    examples: string[];
    retention_period: string;
}

export interface LegalBasisInfo {
    article: string;
    title: string;
    description: string;
}

export interface UserRightInfo {
    right: string;
    title: string;
    description: string;
    how_to_exercise: string;
}

export class CookieConsentService {
    private sessionId: string | null = null;

    constructor() {
        // Generate or retrieve session ID for anonymous users
        this.sessionId = this.getOrCreateSessionId();
    }

    private getOrCreateSessionId(): string {
        let sessionId = localStorage.getItem('cookie_session_id');
        if (!sessionId) {
            sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('cookie_session_id', sessionId);
        }
        return sessionId;
    }

    private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
        // Use relative URLs like other services in the app
        let url = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
        
        // Add session ID to query params if not authenticated
        const urlObj = new URL(url, window.location.origin);
        if (!this.isAuthenticated() && this.sessionId) {
            urlObj.searchParams.set('session_id', this.sessionId);
        }

        const defaultHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        // Add auth token if available
        const token = localStorage.getItem('auth_token');
        if (token) {
            defaultHeaders['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(urlObj.toString(), {
            ...options,
            headers: {
                ...defaultHeaders,
                ...options.headers,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
            throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response;
    }

    private isAuthenticated(): boolean {
        return !!localStorage.getItem('auth_token');
    }

    /**
     * Get current consent status for the user or session
     */
    async getCurrentConsent(): Promise<CookieConsentResponse | null> {
        try {
            const response = await this.makeRequest('/cookies/consent');
            const data = await response.json();
            
            // If consent_id is 0, it means no real consent exists (default response)
            if (data.consent_id === 0) {
                return null;
            }
            
            return data;
        } catch (error) {
            console.warn('Failed to get current consent:', error);
            return null;
        }
    }

    /**
     * Create new cookie consent preferences
     */
    async createConsent(request: CreateConsentRequest): Promise<CookieConsentResponse> {
        const response = await this.makeRequest('/cookies/consent', {
            method: 'POST',
            body: JSON.stringify(request),
        });

        return response.json();
    }

    /**
     * Update existing cookie consent preferences
     */
    async updateConsent(request: UpdateConsentRequest): Promise<CookieConsentResponse> {
        const response = await this.makeRequest('/cookies/consent', {
            method: 'PUT',
            body: JSON.stringify(request),
        });

        return response.json();
    }

    /**
     * Withdraw all cookie consent (GDPR right to withdraw)
     */
    async withdrawConsent(): Promise<void> {
        await this.makeRequest('/cookies/consent', {
            method: 'DELETE',
        });
    }

    /**
     * Check if user has consent for a specific cookie category
     */
    async hasConsentForCategory(category: string): Promise<boolean> {
        try {
            const url = `/cookies/consent/check?category=${encodeURIComponent(category)}`;
            const response = await this.makeRequest(url);
            const data: CategoryConsentResponse = await response.json();
            return data.has_consent && !data.is_expired;
        } catch (error) {
            console.warn(`Failed to check consent for category ${category}:`, error);
            return false;
        }
    }

    /**
     * Get cookie policy information
     */
    async getCookiePolicy(language = 'pt-PT'): Promise<CookiePolicyResponse> {
        const response = await this.makeRequest(`/cookies/policy?lang=${encodeURIComponent(language)}`);
        return response.json();
    }

    /**
     * Transfer session consent to user account (called after login)
     */
    async transferSessionConsent(): Promise<void> {
        if (!this.sessionId || !this.isAuthenticated()) {
            return;
        }

        try {
            await this.makeRequest('/cookies/transfer', {
                method: 'POST',
                body: JSON.stringify({ session_id: this.sessionId }),
            });

            // Clear session ID after successful transfer
            localStorage.removeItem('cookie_session_id');
            this.sessionId = null;
        } catch (error) {
            console.warn('Failed to transfer session consent:', error);
        }
    }

    /**
     * Get consent history (authenticated users only)
     */
    async getConsentHistory(): Promise<any[]> {
        if (!this.isAuthenticated()) {
            throw new Error('Authentication required for consent history');
        }

        const response = await this.makeRequest('/cookies/history');
        return response.json();
    }

    // Utility methods for common consent checks

    /**
     * Check if analytics tracking is allowed
     */
    async canTrackAnalytics(): Promise<boolean> {
        return this.hasConsentForCategory('analytics');
    }

    /**
     * Check if functional cookies are allowed
     */
    async canUseFunctional(): Promise<boolean> {
        return this.hasConsentForCategory('functional');
    }

    /**
     * Check if marketing cookies are allowed
     */
    async canUseMarketing(): Promise<boolean> {
        return this.hasConsentForCategory('marketing');
    }

    /**
     * Check if third-party cookies are allowed
     */
    async canUseThirdParty(): Promise<boolean> {
        return this.hasConsentForCategory('third_party');
    }

    /**
     * Get all consent preferences as an object
     */
    async getAllConsent(): Promise<{
        necessary: boolean;
        analytics: boolean;
        functional: boolean;
        marketing: boolean;
        third_party: boolean;
    }> {
        const consent = await this.getCurrentConsent();
        
        if (!consent || consent.is_expired) {
            // Default to no consent for non-essential cookies
            return {
                necessary: true,
                analytics: false,
                functional: false,
                marketing: false,
                third_party: false,
            };
        }

        return {
            necessary: consent.necessary_cookies,
            analytics: consent.analytics_cookies,
            functional: consent.functional_cookies,
            marketing: consent.marketing_cookies,
            third_party: consent.third_party_cookies,
        };
    }

    /**
     * Subscribe to consent changes (simplified implementation)
     */
    onConsentChange(callback: (consent: any) => void): () => void {
        // This is a simplified implementation
        // In a more complex app, you might use event emitters or observables
        
        const handler = (event: CustomEvent) => {
            callback(event.detail);
        };

        document.addEventListener('cookie-consent-changed', handler as EventListener);
        
        return () => {
            document.removeEventListener('cookie-consent-changed', handler as EventListener);
        };
    }



    /**
     * Clear all stored consent data (for testing/debugging)
     */
    clearStoredConsent(): void {
        localStorage.removeItem('cookie_session_id');
        this.sessionId = this.getOrCreateSessionId();
    }
}

// Singleton instance for global use
export const cookieConsent = new CookieConsentService();

// Global consent checking utilities
export const CookieUtils = {
    /**
     * Quick check if analytics is enabled
     */
    async canTrack(): Promise<boolean> {
        return cookieConsent.canTrackAnalytics();
    },

    /**
     * Initialize Google Analytics if consent is given
     */
    async initializeAnalytics(): Promise<void> {
        if (await cookieConsent.canTrackAnalytics()) {
            // Initialize Google Analytics here
            console.log('Analytics consent granted - initializing tracking');
            // Example: gtag('config', 'GA_MEASUREMENT_ID');
        } else {
            console.log('Analytics consent not granted - tracking disabled');
        }
    },

    /**
     * Load script conditionally based on consent
     */
    async loadConditionalScript(category: 'analytics' | 'functional' | 'marketing' | 'third_party', scriptUrl: string, scriptId?: string): Promise<boolean> {
        const hasConsent = await cookieConsent.hasConsentForCategory(category);
        
        if (!hasConsent) {
            console.log(`Script blocked due to lack of ${category} consent:`, scriptUrl);
            return false;
        }

        // Check if script is already loaded
        if (scriptId && document.getElementById(scriptId)) {
            return true;
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = scriptUrl;
            if (scriptId) script.id = scriptId;
            
            script.onload = () => resolve(true);
            script.onerror = () => reject(new Error(`Failed to load script: ${scriptUrl}`));
            
            document.head.appendChild(script);
        });
    }
};
